import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { todayString } from '../../../utils/dateUtils';
import { useOrderReminders } from '../../../api/orderApi';

export default function TodayReminders() {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<string>>(() => {
    try {
      const saved = sessionStorage.getItem('home_dismissed_reminders');
      return new Set(saved ? JSON.parse(saved) : []);
    } catch {
      return new Set();
    }
  });

  // 取数交给 react-query：一次性的 useEffect 让「详情里删了带提醒的备注」之后首页照旧摆着那条
  const { data: reminders = [], isLoading: loading } = useOrderReminders();

  const dismiss = (id: string) => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        sessionStorage.setItem('home_dismissed_reminders', JSON.stringify([...next]));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const handleViewOrder = (orderId: string) => {
    navigate('/orders', { state: { openOrderId: orderId } });
  };

  const visible = reminders.filter(r => !dismissed.has(r.id));
  const today = todayString();
  const overdue = visible.filter(r => r.remindAt < today);
  const dueToday = visible.filter(r => r.remindAt === today);

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden mb-5 md:mb-8">
      {/* Header */}
      <div className={`flex items-center gap-2.5 px-4 md:px-5 py-3 border-b ${
        visible.length > 0 ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'
      }`}>
        <div className={`w-7 h-7 flex items-center justify-center rounded-lg shrink-0 ${
          visible.length > 0 ? 'bg-amber-100' : 'bg-gray-100'
        }`}>
          <i className={`ri-alarm-warning-line text-sm ${visible.length > 0 ? 'text-amber-500' : 'text-gray-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${visible.length > 0 ? 'text-amber-700' : 'text-gray-600'}`}>
            今日备注提醒
            {!loading && (
              <span className={`ml-2 text-xs font-normal ${visible.length > 0 ? 'text-amber-500' : 'text-gray-400'}`}>
                {visible.length > 0 ? `${visible.length} 条待处理` : '暂无提醒'}
              </span>
            )}
          </p>
          {visible.length > 0 && (overdue.length > 0 || dueToday.length > 0) && (
            <p className="text-xs text-amber-400 mt-0.5">
              {overdue.length > 0 && <span className="text-rose-400">{overdue.length} 条逾期</span>}
              {overdue.length > 0 && dueToday.length > 0 && <span className="mx-1">·</span>}
              {dueToday.length > 0 && <span>{dueToday.length} 条今日到期</span>}
            </p>
          )}
        </div>
        <button
          onClick={() => navigate('/orders')}
          className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap mr-3 hidden sm:block"
        >
          前往订单页 →
        </button>
        {visible.length > 0 && (
          <button
            onClick={() => visible.forEach(r => dismiss(r.id))}
            className="text-xs text-amber-400 hover:text-amber-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            全部忽略
          </button>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="px-4 md:px-5 py-4 flex items-center gap-2">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-loader-4-line animate-spin text-gray-300" />
          </div>
          <span className="text-sm text-gray-300">加载中...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && visible.length === 0 && (
        <div className="px-4 md:px-5 py-5 flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg shrink-0">
            <i className="ri-checkbox-circle-line text-gray-300 text-lg" />
          </div>
          <div>
            <p className="text-sm text-gray-400">今日没有到期提醒</p>
            <p className="text-xs text-gray-300 mt-0.5">在订单备注中设置提醒时间，到期后会在这里显示</p>
          </div>
        </div>
      )}

      {/* Reminders list */}
      {!loading && visible.length > 0 && (
        <div className="divide-y divide-gray-50">
          {visible.slice(0, 6).map(r => {
            const isOverdue = r.remindAt < today;
            return (
              <div key={r.id} className="flex items-start gap-3 px-4 md:px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${isOverdue ? 'bg-rose-400' : 'bg-amber-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${
                      isOverdue ? 'bg-rose-50 text-rose-500' : 'bg-amber-50 text-amber-600'
                    }`}>
                      {isOverdue ? `逾期 ${r.remindAt.slice(5)}` : `今日 ${r.remindAt.slice(5)}`}
                    </span>
                    {r.customerName && (
                      <span className="text-xs text-gray-500 font-medium truncate">{r.customerName}</span>
                    )}
                    {r.orderNumber && (
                      <span className="text-xs text-gray-300 font-mono truncate hidden sm:inline">{r.orderNumber}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">{r.content}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => handleViewOrder(r.orderId)}
                    className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-lg hover:bg-teal-50"
                  >
                    查看订单
                  </button>
                  <button
                    onClick={() => dismiss(r.id)}
                    className="w-6 h-6 flex items-center justify-center text-gray-300 hover:text-gray-500 cursor-pointer transition-colors rounded-lg hover:bg-gray-100"
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              </div>
            );
          })}
          {visible.length > 6 && (
            <div className="px-4 md:px-5 py-2.5 text-center bg-gray-50">
              <button
                onClick={() => navigate('/orders')}
                className="text-xs text-teal-500 hover:text-teal-600 cursor-pointer transition-colors"
              >
                查看全部 {visible.length} 条提醒 →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
